import { ChangeDetectionStrategy, Component, DestroyRef, inject, input, OnInit, ViewEncapsulation } from '@angular/core';
import { asNonUndefined } from '@kaseya-utils';
import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { NavigationLinkComponent } from '@layout/navigation/components/navigation-link/navigation-link.component';
import { TranslatePipe } from '@ngx-translate/core';
import { SpinnerComponent, TagColorVariant, TagComponent } from '@kaseya-design-components';
import { LeftNavigationExternalItem } from '@core/navigation/models/left-navigation/left-navigation-item.interface';
import { ExternalLinkLoadingFacade } from '@core/navigation/facades/external-link-loading.facade';
import { OpenSsoLinkDirective } from '@design/open-sso-link/directives/open-sso-link.directive';
import { OpenSsoCertifyTaxLinkDirective } from '@design/open-sso-link/directives/open-sso-certify-tax-link.directive';
import { OpenSsoDeepLinkDirective } from '@design/open-sso-link/directives/open-sso-deep-link.directive';
import { BehaviorSubject, Observable, Subject, filter, finalize, takeUntil } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'itc-navigation-external-tagged-link',
  templateUrl: 'navigation-external-tagged-link.component.html',
  styleUrl: 'navigation-external-tagged-link.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NavigationLinkComponent,
    TranslatePipe,
    OpenSsoLinkDirective,
    OpenSsoCertifyTaxLinkDirective,
    OpenSsoDeepLinkDirective,
    AsyncPipe,
    NgTemplateOutlet,
    SpinnerComponent,
    TagComponent,
  ],
  encapsulation: ViewEncapsulation.None,
})
export class NavigationExternalTaggedLinkComponent implements OnInit {
  private readonly externalLinkLoadingFacade = inject(ExternalLinkLoadingFacade);
  private readonly loaded$ = new Subject<void>();
  private readonly destroyRef = inject(DestroyRef);

  readonly navigationItem = input.required<LeftNavigationExternalItem>();
  readonly isTooltipEnabled = input.required<boolean>();
  readonly tagTextTranslationKey = input<string>();
  readonly tagColorVariant = input<TagColorVariant>();
  readonly isLoading$ = new BehaviorSubject(false);

  ngOnInit(): void {
    this.updateLoadingStateOnExternalLinkLoading();
  }

  openExternalLink(): void {
    if (this.isLoading$.value) {
      return;
    }

    this.setLoadingState(true);
    asNonUndefined<Observable<string>>(this.navigationItem().externalLink)
      .pipe(
        filter(Boolean),
        finalize(() => {
          this.setLoadingState(false);
          this.completeLinkLoading();
        }),
        takeUntil(this.loaded$)
      )
      .subscribe(link => window.open(link));
  }

  private setLoadingState(loading: boolean): void {
    this.externalLinkLoadingFacade.setExternalLinkLoading(this.navigationItem().id, loading);
  }

  private updateLoadingStateOnExternalLinkLoading(): void {
    this.externalLinkLoadingFacade
      .isExternalLinkLoading$(this.navigationItem().id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(loading => this.isLoading$.next(loading));
  }

  private completeLinkLoading(): void {
    this.loaded$.next();
    this.loaded$.complete();
  }
}
